// src/pages/WebsiteTermsPage.jsx
import React from 'react';
import useLegalLanguage from '../hooks/useLegalLanguage';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { PageTitle, InfoBlock, LanguageToggle, LangButton, LegalContactBlock, ContactBlock } from '../components/ui/PageElements';
import { email as siteEmail, businessInfo, hostingInfo, legalLabels } from '../utils/generalData';
import PageMeta from '../components/PageMeta';

const LegalWrapper = styled.div`
    max-width: 800px;
    margin: 50px auto 100px;
    padding: 0 20px;
    color: var(--text-color);
    line-height: 1.7;

    h2 {
        font-size: 24px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 1px;
        margin-top: 40px;
        margin-bottom: 20px;
        color: var(--primary-color);
        position: relative;
        &:before {
            content: "§";
            color: var(--secondary-color);
            margin-right: 10px;
            opacity: 0.7;
        }
    }

    p, li {
        font-size: 16px;
        color: #cccccc;
        margin-bottom: 15px;
    }

    ul {
        list-style: disc;
        padding-left: 30px;
        margin-bottom: 20px;
    }

    a {
        color: var(--secondary-color);
        text-decoration: underline;
        text-underline-offset: 3px;
        &:hover {
            color: var(--primary-color);
        }
    }

    strong {
        color: var(--text-color);
        font-weight: 700;
    }
`;

const LastUpdatedText = styled.p`
    font-style: italic;
    color: var(--text-muted);
    margin-bottom: 30px !important;
    font-size: 14px !important;
`;

const CURRENT_DATE = new Date().toLocaleDateString('en-CA');

const content = {
    en: {
        title: "Terms of Use & Legal Notice",
        lastUpdated: "Last Updated",
        intro: `Welcome to lperron.com. This page covers both the terms governing your use of this website
            and the legal information I'm required to provide under French law (since I'm based in France).
            I've kept it straightforward: no walls of incomprehensible legal text.`,

        // Section 1: Who I Am
        section1Title: "1. Who I Am",
        section1Intro: "Let's start with the basics. Here's who's behind this website:",
        section1DirectorTitle: "Director of Publication",
        section1DirectorIntro: "That's me, ",
        section1DirectorDesc: ". I'm responsible for what gets published here.",

        // Section 2: Hosting
        section2Title: "2. Website Hosting",
        section2Intro: "This site is hosted by:",

        // Section 3: VAT
        section3Title: "3. VAT Status",
        section3Content: `As a micro-entrepreneur in France, I benefit from the VAT exemption scheme.
            In plain terms: I don't charge VAT on my services.`,

        // Section 4: Using This Website
        section4Title: "4. Using This Website",
        section4Intro: `This is my portfolio and a place where I sell some of my software projects.
            By using this site, you agree to a few reasonable things:`,
        section4Items: [
            "Use the site lawfully and respectfully",
            "Don't try to break, hack, or overload anything",
            "Don't scrape content or use bots without permission",
            "Respect the intellectual property of the work showcased here"
        ],
        section4Outro: "Basically: be a decent human being, and we're good.",

        // Section 5: Intellectual Property
        section5Title: "5. Intellectual Property",
        section5Content: `Everything on this site (the code, designs, text, images, logos, and projects) is my work
            (or used with permission) and is protected by intellectual property laws. You're welcome to look,
            be inspired, and reach out if you want to collaborate. But please don't copy, redistribute,
            or claim any of it as your own.`,

        // Section 6: Disclaimers
        section6Title: "6. Disclaimers & Liability",
        section6Content1: `I do my best to keep information accurate and the site running smoothly,
            but I can't guarantee perfection. The content is provided "as is."`,
        section6Content2: `I'm not liable for any damages resulting from using (or being unable to use) this website.
            If you find an error or something broken, I'd appreciate you letting me know so I can fix it.`,

        // Section 7: External Links
        section7Title: "7. External Links",
        section7Content: `This site may link to third-party websites (GitLab, payment processors, etc.).
            I don't control those sites and can't be held responsible for their content or practices.
            When you click an external link, you're leaving my site. Check their terms and privacy policies.`,

        // Section 8: Personal Data
        section8Title: "8. Personal Data",
        section8Content1: "For all the details on how I handle (or rather, mostly don't handle) your data, see my ",
        section8PrivacyLink: "Privacy Policy",
        section8Content2: `. The short version: I collect minimal data, don't track you across the web,
            and respect your privacy. Any data you provide (like through the contact form) is used only
            to respond to you.`,

        // Section 9: Cookies
        section9Title: "9. Cookies",
        section9Content: `This site uses only essential technical cookies required for basic functionality.
            No tracking cookies, no advertising cookies, no cookie consent banner pestering you on every visit.
            More details in my `,

        // Section 10: Governing Law
        section10Title: "10. Governing Law",
        section10Content: `These terms are governed by French law. If any dispute arises that we can't resolve amicably,
            the courts of Lyon, France will have jurisdiction.`,

        // Section 11: Changes
        section11Title: "11. Changes to These Terms",
        section11Content: `I may update these terms occasionally. When I do, I'll update the "Last Updated" date at the top.
            Continued use of the site after changes means you accept the new terms.`,

        // Section 12: Contact
        section12Title: "12. Contact",
        section12Intro: "Questions about these terms? Want to report an issue? Here's how to reach me:"
    },

    fr: {
        title: "Conditions d'Utilisation & Mentions Légales",
        lastUpdated: "Dernière mise à jour",
        intro: `Bienvenue sur lperron.com. Cette page regroupe les conditions d'utilisation de ce site
            ainsi que les mentions légales obligatoires en France. J'ai voulu rester simple et direct : pas
            de murs de texte juridique incompréhensible.`,

        // Section 1: Who I Am
        section1Title: "1. Qui suis-je",
        section1Intro: "Commençons par les bases. Voici qui est derrière ce site :",
        section1DirectorTitle: "Directeur de la publication",
        section1DirectorIntro: "C'est moi, ",
        section1DirectorDesc: ". Je suis responsable de ce qui est publié ici.",

        // Section 2: Hosting
        section2Title: "2. Hébergement du site",
        section2Intro: "Ce site est hébergé par :",

        // Section 3: VAT
        section3Title: "3. TVA",
        section3Content: `En tant que micro-entrepreneur en France, je bénéficie de la franchise en base de TVA.
            Concrètement : je ne facture pas de TVA sur mes prestations.`,

        // Section 4: Using This Website
        section4Title: "4. Utilisation du site",
        section4Intro: `Ce site est mon portfolio et un espace où je vends certains de mes projets logiciels.
            En utilisant ce site, vous acceptez quelques règles de bon sens :`,
        section4Items: [
            "Utiliser le site de manière légale et respectueuse",
            "Ne pas tenter de casser, pirater ou surcharger quoi que ce soit",
            "Ne pas scraper le contenu ou utiliser des bots sans permission",
            "Respecter la propriété intellectuelle des travaux présentés"
        ],
        section4Outro: "En résumé : comportez-vous correctement, et tout ira bien.",

        // Section 5: Intellectual Property
        section5Title: "5. Propriété intellectuelle",
        section5Content: `Tout ce qui se trouve sur ce site (le code, les designs, les textes, images, logos
            et projets) est mon travail (ou utilisé avec permission) et est protégé par le droit de la propriété
            intellectuelle. Vous êtes libre de regarder, de vous inspirer et de me contacter si vous souhaitez
            collaborer. Mais merci de ne pas copier, redistribuer ou vous approprier quoi que ce soit.`,

        // Section 6: Disclaimers
        section6Title: "6. Limitation de responsabilité",
        section6Content1: `Je fais de mon mieux pour maintenir des informations exactes et un site fonctionnel,
            mais je ne peux pas garantir la perfection. Le contenu est fourni "en l'état".`,
        section6Content2: `Je ne suis pas responsable des dommages résultant de l'utilisation
            (ou de l'impossibilité d'utiliser) ce site. Si vous trouvez une erreur ou quelque chose de cassé,
            je vous serais reconnaissant de me le signaler pour que je puisse le corriger.`,

        // Section 7: External Links
        section7Title: "7. Liens externes",
        section7Content: `Ce site peut contenir des liens vers des sites tiers (GitLab, processeurs de paiement, etc.).
            Je ne contrôle pas ces sites et ne peux être tenu responsable de leur contenu ou de leurs pratiques.
            Quand vous cliquez sur un lien externe, vous quittez mon site. Consultez leurs conditions et politiques de confidentialité.`,

        // Section 8: Personal Data
        section8Title: "8. Données personnelles",
        section8Content1: "Pour tous les détails sur comment je gère (ou plutôt, ne gère pas) vos données, consultez ma ",
        section8PrivacyLink: "Politique de Confidentialité",
        section8Content2: `. En bref : je collecte un minimum de données, je ne vous traque pas sur le web,
            et je respecte votre vie privée. Toute donnée que vous fournissez (comme via le formulaire de contact)
            est utilisée uniquement pour vous répondre.`,

        // Section 9: Cookies
        section9Title: "9. Cookies",
        section9Content: `Ce site utilise uniquement des cookies techniques essentiels au fonctionnement de base.
            Pas de cookies de traçage, pas de cookies publicitaires, pas de bannière de consentement vous harcelant
            à chaque visite. Plus de détails dans ma `,

        // Section 10: Governing Law
        section10Title: "10. Droit applicable",
        section10Content: `Ces conditions sont régies par le droit français. En cas de litige que nous ne pourrions
            résoudre à l'amiable, les tribunaux de Lyon seront compétents.`,

        // Section 11: Changes
        section11Title: "11. Modifications",
        section11Content: `Je peux mettre à jour ces conditions occasionnellement. Le cas échéant, je mettrai à jour
            la date "Dernière mise à jour" en haut de page. Continuer à utiliser le site après des modifications
            signifie que vous acceptez les nouvelles conditions.`,

        // Section 12: Contact
        section12Title: "12. Contact",
        section12Intro: "Des questions sur ces conditions ? Un problème à signaler ? Voici comment me joindre :"
    }
};

const WebsiteTermsPage = () => {
    const [lang, setLang] = useLegalLanguage();
    const t = content[lang];

    return (
        <LegalWrapper>
            <PageMeta
                title="Terms of Use"
                description="Terms of Use and Legal Notice for lperron.com. Legal terms governing your use of this website."
                canonicalPath="/terms-of-use"
            />
            <LanguageToggle>
                <LangButton $active={lang === 'en'} onClick={() => setLang('en')}>EN</LangButton>
                <LangButton $active={lang === 'fr'} onClick={() => setLang('fr')}>FR</LangButton>
            </LanguageToggle>
            <PageTitle style={{ textAlign: 'left', fontSize: '36px', marginBottom: '10px' }}>{t.title}</PageTitle>
            <LastUpdatedText>{t.lastUpdated}: {CURRENT_DATE}</LastUpdatedText>

            <p>{t.intro}</p>

            {/* Section 1: Who I Am */}
            <h2>{t.section1Title}</h2>
            <p>{t.section1Intro}</p>
            <LegalContactBlock lang={lang} />
            <p>
                <strong>{t.section1DirectorTitle}:</strong> {t.section1DirectorIntro}
                <strong>{businessInfo.name}</strong>{t.section1DirectorDesc}
            </p>

            {/* Section 2: Hosting */}
            <h2>{t.section2Title}</h2>
            <p>{t.section2Intro}</p>
            <InfoBlock>
                <p><strong>{hostingInfo.name}</strong></p>
                <p>{hostingInfo.address.street}</p>
                <p>{hostingInfo.address.city}</p>
                <p>{hostingInfo.address.country}</p>
                <p><strong>{legalLabels[lang].website}</strong> <a href={hostingInfo.website} target="_blank" rel="noopener noreferrer">{hostingInfo.website.replace('https://', '')}</a></p>
            </InfoBlock>

            {/* Section 3: VAT */}
            <h2>{t.section3Title}</h2>
            <p>{t.section3Content}</p>
            <p><strong>{businessInfo.vatNotice[lang]}</strong></p>

            {/* Section 4: Using This Website */}
            <h2>{t.section4Title}</h2>
            <p>{t.section4Intro}</p>
            <ul>
                {t.section4Items.map((item, index) => (
                    <li key={index}>{item}</li>
                ))}
            </ul>
            <p>{t.section4Outro}</p>

            {/* Section 5: Intellectual Property */}
            <h2>{t.section5Title}</h2>
            <p>{t.section5Content}</p>

            {/* Section 6: Disclaimers */}
            <h2>{t.section6Title}</h2>
            <p>{t.section6Content1}</p>
            <p>{t.section6Content2}</p>

            {/* Section 7: External Links */}
            <h2>{t.section7Title}</h2>
            <p>{t.section7Content}</p>

            {/* Section 8: Personal Data */}
            <h2>{t.section8Title}</h2>
            <p>
                {t.section8Content1}
                <Link to="/privacy-policy">{t.section8PrivacyLink}</Link>
                {t.section8Content2}
            </p>

            {/* Section 9: Cookies */}
            <h2>{t.section9Title}</h2>
            <p>
                {t.section9Content}
                <Link to="/privacy-policy">{t.section8PrivacyLink}</Link>.
            </p>

            {/* Section 10: Governing Law */}
            <h2>{t.section10Title}</h2>
            <p>{t.section10Content}</p>

            {/* Section 11: Changes */}
            <h2>{t.section11Title}</h2>
            <p>{t.section11Content}</p>

            {/* Section 12: Contact */}
            <h2>{t.section12Title}</h2>
            <p>{t.section12Intro}</p>
            <ContactBlock lang={lang} />

        </LegalWrapper>
    );
};

export default WebsiteTermsPage;
